// ============================================================
// DM-LMS — App Router
// Role-based routing: /app/student | /app/tutor | /app/admin
// Auth guard applied at layout level — DRY, not per-page.
// ============================================================

import { Routes, Route, Navigate, useParams }  from "react-router-dom";
import { useAuth }                   from "./hooks/useAuth.js";
import { ProfileProvider }  from "./contexts/ProfileContext.js";
import { CoursesProvider }  from "./contexts/CoursesContext.js";
import { SessionsProvider } from "./contexts/SessionsContext.js";

// Layouts
import AuthLayout    from "./components/layout/AuthLayout.js";
import StudentLayout from "./components/layout/StudentLayout.js";
import TutorLayout   from "./components/layout/TutorLayout.js";
import AdminLayout   from "./components/layout/AdminLayout.js";

// Auth pages
import LoginPage           from "./pages/auth/LoginPage.js";

// Student pages
import StudentDashboard   from "./pages/student/DashboardPage.js";
import StudentCourses     from "./pages/student/CoursesPage.js";
import StudentAttendance  from "./pages/student/AttendancePage.js";
import StudentPayments    from "./pages/student/PaymentsPage.js";
import StudentCertificates from "./pages/student/CertificatesPage.js";
import StudentProfileSetup from "./pages/student/ProfileSetupPage.js";
import StudentProfile     from "./pages/student/ProfilePage.js";

// Tutor pages
import TutorDashboard     from "./pages/tutor/DashboardPage.js";
import TutorCourses       from "./pages/tutor/CoursesPage.js";
import TutorSessions      from "./pages/tutor/SessionsPage.js";
import TutorSessionDetail from "./pages/tutor/SessionDetailPage.js";
import TutorAttendance    from "./pages/tutor/AttendancePage.js";
import TutorRoster        from "./pages/tutor/RosterPage.js";
import TutorProfile       from "./pages/tutor/ProfilePage.js";

// Admin pages
import AdminDashboard    from "./pages/admin/DashboardPage.js";
import AdminUsers        from "./pages/admin/UsersPage.js";
import AdminCourses      from "./pages/admin/CoursesPage.js";
import AdminCourseDetail from "./pages/admin/CourseDetailPage.js";
import AdminSessions     from "./pages/admin/SessionsPage.js";
import AdminSessionDetail from "./pages/admin/SessionDetailPage.js";
import AdminEnrollments  from "./pages/admin/EnrollmentsPage.js";
import AdminPayments     from "./pages/admin/PaymentsPage.js";
import AdminCertificates from "./pages/admin/CertificatesPage.js";
import AdminSystemConfig from "./pages/admin/SystemConfigPage.js";
import AdminUserDetail   from "./pages/admin/UserDetailPage.js";
import AdminActivity     from "./pages/admin/ActivityPage.js";
import AdminProfile      from "./pages/admin/ProfilePage.js";

// Role guard — DRY: one component, used by every layout
import RequireRole from "./components/auth/RequireRole.js";
import RequireCompleteProfile from "./components/auth/RequireCompleteProfile.js";

// DEV ONLY: floating role switcher, tree-shaken out of production builds
import DevRoleSwitcher from "./components/dev/DevRoleSwitcher.js";

// /app/admin/students/:studentId was the old student detail URL; it is now /app/admin/users/:id.
function StudentDetailRedirect() {
  const { studentId } = useParams<{ studentId: string }>();
  return <Navigate to={`/app/admin/users/${encodeURIComponent(studentId ?? "")}`} replace />;
}

export default function App() {
  const { user, isLoading } = useAuth();

  if (isLoading) return <div className="dm-loading">Loading...</div>;

  return (
    <>
      <Routes>
        {/* Public auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/app/login"            element={<LoginPage />} />
        </Route>

        {/* Student profile setup — full-screen, no sidebar. Guarded by role
            only: RequireCompleteProfile itself would redirect here forever
            if it also wrapped this route. */}
        <Route element={<RequireRole role="student"><AuthLayout /></RequireRole>}>
          <Route path="/app/student/profile-setup" element={<StudentProfileSetup />} />
        </Route>

        {/* Student routes */}
        <Route
          element={
            <RequireRole role="student">
              <ProfileProvider role="student">
                <RequireCompleteProfile>
                  <CoursesProvider>
                    <StudentLayout />
                  </CoursesProvider>
                </RequireCompleteProfile>
              </ProfileProvider>
            </RequireRole>
          }
        >
          <Route path="/app/student"              element={<StudentDashboard />} />
          <Route path="/app/student/courses"      element={<StudentCourses />} />
          <Route path="/app/student/attendance"   element={<StudentAttendance />} />
          <Route path="/app/student/payments"     element={<StudentPayments />} />
          <Route path="/app/student/certificates" element={<StudentCertificates />} />
          <Route path="/app/student/profile"      element={<StudentProfile />} />
        </Route>

        {/* Tutor routes */}
        <Route
          element={
            <RequireRole role="tutor">
              <ProfileProvider role="tutor">
                <CoursesProvider>
                  <SessionsProvider>
                    <TutorLayout />
                  </SessionsProvider>
                </CoursesProvider>
              </ProfileProvider>
            </RequireRole>
          }
        >
          <Route path="/app/tutor"                    element={<TutorDashboard />} />
          <Route path="/app/tutor/courses"            element={<TutorCourses />} />
          <Route path="/app/tutor/sessions"           element={<TutorSessions />} />
          <Route path="/app/tutor/sessions/:sessionId" element={<TutorSessionDetail />} />
          <Route path="/app/tutor/attendance"         element={<TutorAttendance />} />
          <Route path="/app/tutor/roster"             element={<TutorRoster />} />
          <Route path="/app/tutor/profile"            element={<TutorProfile />} />
        </Route>

        {/* Admin routes */}
        <Route
          element={
            <RequireRole role="admin">
              <ProfileProvider role="admin">
                <CoursesProvider>
                  <SessionsProvider>
                    <AdminLayout />
                  </SessionsProvider>
                </CoursesProvider>
              </ProfileProvider>
            </RequireRole>
          }
        >
          <Route path="/app/admin"                element={<AdminDashboard />} />
          <Route path="/app/admin/users"          element={<AdminUsers />} />
          <Route path="/app/admin/users/:id"      element={<AdminUserDetail />} />
          <Route path="/app/admin/courses"        element={<AdminCourses />} />
          <Route path="/app/admin/courses/:id"    element={<AdminCourseDetail />} />
          <Route path="/app/admin/sessions"       element={<AdminSessions />} />
          <Route path="/app/admin/sessions/:sessionId" element={<AdminSessionDetail />} />
          <Route path="/app/admin/enrollments"    element={<AdminEnrollments />} />
          <Route path="/app/admin/payments"       element={<AdminPayments />} />
          <Route path="/app/admin/certificates"   element={<AdminCertificates />} />
          {/* Old Students / Tutors pages now live on Users; keep bookmarks working. */}
          <Route path="/app/admin/students"       element={<Navigate to="/app/admin/users?role=student" replace />} />
          <Route path="/app/admin/students/:studentId" element={<StudentDetailRedirect />} />
          <Route path="/app/admin/tutors"         element={<Navigate to="/app/admin/users?role=tutor" replace />} />
          <Route path="/app/admin/activity"       element={<AdminActivity />} />
          <Route path="/app/admin/profile"        element={<AdminProfile />} />
          <Route path="/app/admin/config"         element={<AdminSystemConfig />} />
        </Route>

        {/* Smart root redirect — after Google OAuth callback, straight to the role dashboard
            (students without a complete profile are then sent to profile-setup by their guard). */}
        <Route
          path="/app"
          element={
            !user ? (
              <Navigate to="/app/login" replace />
            ) : (
              <Navigate to={`/app/${user.role}`} replace />
            )
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>

      {/* DEV ONLY: floating role switcher */}
      {import.meta.env.DEV && <DevRoleSwitcher />}
    </>
  );
}
